import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import api from './services/api';
import { captureAttribution, readUtmFromUrl } from './utils/attribution';
import { FONT } from './styles/tokens';
import Footer from './components/Layout/Footer';
import { AuthProvider, useAuth } from './context/AuthContext';
import LandingPage from './pages/LandingPage';
import ExplorePage from './pages/ExplorePage';
import { ecrireCriteres, lireCriteres } from './components/Affaires/criteres';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import AlertsPage from './pages/AlertsPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import ListingDetailPage from './pages/ListingDetailPage';
import FavoritesPage from './pages/FavoritesPage';
import HistoryPage from './pages/HistoryPage';
import CGUPage from './pages/CGUPage';
import PolitiqueConfidentialitePage from './pages/PolitiqueConfidentialitePage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import ContactPage from './pages/ContactPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';


import TrouverPage from './pages/TrouverPage';
import VendrePage from './pages/VendrePage';
import CGUGaragePage from './pages/CGUGaragePage';
import GarageInscriptionPage from './pages/GarageInscriptionPage';
import GarageDepotsPage from './pages/GarageDepotsPage';
/* ── Intro splash — scan détective ────────────────────────────────────────── */
const INTRO_KEYFRAMES = `
  @keyframes zkScan {
    from { transform: translateX(-2px); }
    to   { transform: translateX(calc(100vw + 2px)); }
  }
  @keyframes zkReveal {
    from { opacity: 0; filter: blur(8px); }
    to   { opacity: 1; filter: blur(0px); }
  }
  @keyframes zkSlogan {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes zkLoupe {
    from { opacity: 0; transform: scale(0.88); }
    to   { opacity: 0.13; transform: scale(1); }
  }
`;
const INTRO_LETTERS = ['Z','E','N','K','A','R','T'];
const INTRO_DELAYS  = [0.26, 0.36, 0.45, 0.54, 0.63, 0.72, 0.81];

function SplashIntro({ onDone }) {
  const done = useRef(false);
  const [hiding, setHiding] = useState(false);

  const skip = useCallback(() => {
    if (done.current) return;
    done.current = true;
    setHiding(true);
    setTimeout(onDone, 300);
  }, [onDone]);

  useEffect(() => {
    const s = document.createElement('style');
    s.textContent = INTRO_KEYFRAMES;
    document.head.appendChild(s);
    const t = setTimeout(skip, 1800);
    return () => { s.remove(); clearTimeout(t); };
  }, [skip]);

  return (
    <div
      onClick={skip}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#F3EEE6',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 'clamp(14px, 2.5vw, 24px)',
        opacity: hiding ? 0 : 1,
        transition: 'opacity 0.3s ease',
        pointerEvents: hiding ? 'none' : 'auto',
        userSelect: 'none',
        cursor: 'default',
        overflow: 'hidden',
      }}
    >
      {/* Ligne de scan orange */}
      <div style={{
        position: 'absolute',
        top: 0, bottom: 0, left: 0,
        width: 2,
        background: 'linear-gradient(to bottom, transparent 5%, #E06A1A 30%, #E06A1A 70%, transparent 95%)',
        boxShadow: '0 0 20px 8px rgba(224,106,26,0.40), 0 0 5px 1px #E06A1A',
        animation: 'zkScan 1.0s linear forwards',
        zIndex: 2,
        pointerEvents: 'none',
        willChange: 'transform',
      }} />

      {/* ZENKART + loupe derrière */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

        {/* Loupe SVG — watermark subtil */}
        <div style={{
          position: 'absolute',
          zIndex: 0,
          pointerEvents: 'none',
          animation: 'zkLoupe 0.55s ease-out 0.90s both',
        }}>
          <svg
            viewBox="0 0 110 100"
            style={{ width: 'clamp(200px, 46vw, 380px)', display: 'block' }}
            fill="none"
            aria-hidden="true"
          >
            <circle cx="42" cy="42" r="36" stroke="#1B4D8F" strokeWidth="1.3" />
            <line x1="68" y1="68" x2="90" y2="88"
              stroke="#1B4D8F" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>

        {/* Lettres ZENKART */}
        <div style={{
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'baseline',
          gap: 'clamp(1px, 0.6vw, 5px)',
        }}>
          {INTRO_LETTERS.map((l, i) => (
            <span key={i} style={{
              fontFamily: FONT,
              fontSize: 'clamp(46px, 9.5vw, 88px)',
              fontWeight: 700,
              color: '#1B4D8F',
              letterSpacing: '-0.02em',
              lineHeight: 1,
              display: 'inline-block',
              animation: 'zkReveal 0.18s ease-out both',
              animationDelay: `${INTRO_DELAYS[i]}s`,
            }}>
              {l}
            </span>
          ))}
        </div>
      </div>

      {/* Slogan */}
      <p style={{
        fontFamily: FONT,
        fontSize: 'clamp(11px, 1.6vw, 15px)',
        fontWeight: 500,
        color: '#6B6258',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        margin: 0,
        position: 'relative', zIndex: 1,
        animation: 'zkSlogan 0.4s ease-out both',
        animationDelay: '0.95s',
      }}>
        On enquête, vous économisez
      </p>
    </div>
  );
}

/* ── App Loader (auth wait) ── */
function AppLoader() {
  return (
    <div className="app-loader">
      <div className="app-loader-icon">Z</div>
      <div className="app-loader-bar">
        <div className="app-loader-fill" />
      </div>
      <p className="app-loader-text">CHARGEMENT</p>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AppLoader />;
  return user ? children : <Navigate to="/login" replace />;
}

/* /garage était la page du Garage Pass (2026-10-22 : plus d'abonnement). Elle
   renvoie vers l'espace pro : les dépôts pour un garage validé, l'inscription
   pour tous les autres. GaragePage.js reste dans le code, sans route. */
function VersEspaceGarage() {
  const { user, loading } = useAuth();
  if (loading) return <AppLoader />;
  const valide = user && user.siret_verifie && user.valide_par_admin;
  return <Navigate to={valide ? '/garage/depots' : '/garage/inscription'} replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AppLoader />;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

/* ⚠️ LE CURSEUR PERSONNALISE A ETE RETIRE (2026-09-10).
   Un point orange #FF6B35 et un halo dore #FFD700 remplacaient le vrai
   curseur, masque par `cursor: none !important`, et une boucle
   requestAnimationFrame tournait en permanence pour faire suivre le halo.

   Trois raisons de le supprimer, pas une : les deux couleurs sont hors
   charte (restes du theme sombre-dore d'avant 2026-09-03), un curseur
   d'agrement contredit « aucun ornement », et masquer le curseur systeme
   degrade la precision de pointage pour tout le monde. */

/* ── /listings -> /explore, en traduisant les anciens paramètres ── */
function VersExplore() {
  const location = useLocation();
  const recherche = ecrireCriteres(lireCriteres(location.search));
  return <Navigate to={recherche ? `/explore?${recherche}` : '/explore'} replace />;
}

/* ── Visit tracker (compteur de visiteurs uniques, stocké en base — sans GA) ── */
function VisitTracker() {
  const location = useLocation();

  /* Première touche mémorisée AVANT tout le reste : si le visiteur clique
     immédiatement sur "S'inscrire", l'origine doit déjà être en mémoire. */
  useEffect(() => { captureAttribution(); }, []);

  useEffect(() => {
    /* Les paramètres envoyés sont ceux de l'URL COURANTE, pas la première
       touche stockée : une ligne dans `visits` doit valoir une arrivée réelle.
       Naviguer ensuite dans le site change le chemin sans la query, donc rien
       n'est compté deux fois. */
    const utm = readUtmFromUrl(location.search);
    api.post('/api/track/visit', {
      path: location.pathname,
      ...utm,
      referrer: document.referrer || null,
    }).catch(() => {});
  }, [location.pathname, location.search]);

  return null;
}

export default function App() {
  const [intro, setIntro] = useState(true);
  return (
    <AuthProvider>
      {intro && <SplashIntro onDone={() => setIntro(false)} />}
      <BrowserRouter>
        <VisitTracker />

        <Routes>
          {/* Accueil = la recherche d'abord (refonte AutoUncle du 2026-09-14) :
              le selecteur de profil est parti. /explore garde encore la
              landing longue, en attendant de devenir la liste publique. */}
          <Route path="/"        element={<LandingPage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/login"   element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          {/* Plus d'abonnement depuis le 2026-10-22 : /pricing renvoie vers les
              bonnes affaires. SubscriptionPage.js reste dans le code, sans route. */}
          <Route path="/pricing" element={<Navigate to="/explore" replace />} />
          {/* « Trouvez ma voiture » (2026-10-12) : apercu public (combien
              d'affaires correspondent, 2-3 exemples sans lien ni source), puis
              demande recontactee a la main. Remplace « Verifier » dans la
              navigation, l'accueil et le pied de page. */}
          <Route path="/trouver" element={<TrouverPage />} />
          {/* Premier nom de la route, garde pour les liens deja partis. */}
          <Route path="/trouvez-moi-une-voiture" element={<Navigate to="/trouver" replace />} />
          {/* Vérificateur SUPPRIMÉ le 2026-10-15 : l'adresse renvoie vers
              /trouver, pour les liens déjà partis. Le service d'estimation
              (niveaux_prix) reste : /vendre et la fiche annonce en dépendent. */}
          <Route path="/verifier" element={<Navigate to="/trouver" replace />} />
          {/* « Vendez votre voiture » (2026-09-16) : estimation publique, puis
              dépôt qualifié à la main avant transmission aux garages. */}
          <Route path="/vendre"   element={<VendrePage />} />

          {/* Pages orphelines redirigées le 2026-09-14 : plus aucun lien du site
              ni du serveur n'y menait (connexion et inscription vont vers
              /explore ou /garage). Redirections gardées pour les liens anciens. */}
          <Route path="/onboarding"   element={<Navigate to="/dashboard" replace />} />
          <Route path="/onboarding-particulier" element={<Navigate to="/dashboard" replace />} />
          <Route path="/preference-setup" element={<Navigate to="/explore" replace />} />
          {/* « Jeune permis » = les bonnes affaires à moins de 5 000 €. */}
          <Route path="/jeune-permis" element={<Navigate to="/explore?budget=b5" replace />} />
          {/* Négociateur IA : caché depuis le 2026-07-12, jamais relié. Le fichier
              NegotiatorPage.js reste en réserve, la route ne l'expose plus. */}
          <Route path="/negotiator"   element={<Navigate to="/explore" replace />} />
          {/* /swipe supprimee le 2026-09-03 ; redirection conservee, des liens
              ont pu etre partages. Vers /listings SANS sort=marge : un profil
              acheteur n'a pas de marge a trier. */}
          <Route path="/swipe"        element={<Navigate to="/explore" replace />} />
          <Route path="/dashboard"    element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          {/* /listings a fusionné avec /explore le 2026-09-14 : une seule liste,
              c'est la connexion qui décide de ce que porte la carte. Les liens
              partagés gardent leurs filtres (sort=marge compris). */}
          <Route path="/listings"     element={<VersExplore />} />
          {/* Fiche ouverte aux visiteurs (2026-09-14) : tout sauf le lien et la source. */}
          <Route path="/listings/:id" element={<ListingDetailPage />} />
          <Route path="/alerts"       element={<PrivateRoute><AlertsPage /></PrivateRoute>} />
          <Route path="/profile"      element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/admin"        element={<PrivateRoute><AdminPage /></PrivateRoute>} />
          {/* Affiliation masquée le 2026-10-22 : ses commissions étaient 30 % du
              prix d'offres qui n'existent plus. Codes, parrainages et commissions
              restent en base ; AffiliationPage et PartenairesPage restent dans le code. */}
          <Route path="/affiliation"  element={<Navigate to="/profile" replace />} />
          <Route path="/garage"       element={<VersEspaceGarage />} />
          <Route path="/favoris"      element={<PrivateRoute><FavoritesPage /></PrivateRoute>} />
          <Route path="/historique"   element={<PrivateRoute><HistoryPage /></PrivateRoute>} />

          <Route path="/partenaires"              element={<Navigate to="/explore" replace />} />
          <Route path="/cgu"                      element={<CGUPage />} />
          {/* Conditions garages : acceptation obligatoire à l'inscription garage,
              c'est ce qui rend opposable l'interdiction de réutiliser les
              coordonnées d'un vendeur (2026-09-17). */}
          <Route path="/cgu-garage"               element={<CGUGaragePage />} />
          {/* Demande d'accès garage : SIRET vérifié automatiquement, puis
              validation à la main (2026-09-17). */}
          <Route path="/garage/inscription"       element={<GarageInscriptionPage />} />
          {/* Dépôts visibles d'un garage validé. Le serveur exige SIRET vérifié
              ET validation manuelle : la garde React n'est qu'un confort. */}
          <Route path="/garage/depots"            element={<PrivateRoute><GarageDepotsPage /></PrivateRoute>} />
          <Route path="/confidentialite"          element={<PolitiqueConfidentialitePage />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
          <Route path="/mentions-legales"         element={<MentionsLegalesPage />} />
          <Route path="/contact"                  element={<ContactPage />} />
          <Route path="/forgot-password"          element={<ForgotPasswordPage />} />
          <Route path="/reset-password"           element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Le pied de page est monte ICI, sous les routes, et non dans chaque
            page : c'est la seule facon qu'il soit vraiment sur les 28. Il
            n'etait present que sur 4 d'entre elles avant le 2026-09-10. */}
        <Footer />
      </BrowserRouter>
    </AuthProvider>
  );
}
